/* eslint-disable @next/next/no-img-element */
import React, { useRef } from 'react'
import NewButton from 'components/elements/NewButton'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import { KnowunityNumbers } from 'lib/constants/KnowunityNumbers'
import { parseLocale, localizeNumber } from 'lib/constants/localization'
import _useTranslation from 'lib/hooks/_useTranslation'
import { useRouter } from 'next/router'
import { Headline3, Body2, Headline1 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled, { keyframes } from 'styled-components'
import FlameFrame from 'assets/homepage-redesign/frames/flame-frame.webp'
import HeartFrame from 'assets/homepage-redesign/frames/heart-frame.webp'
import GraduationFrame from 'assets/homepage-redesign/frames/graduation-frame.webp'
import CheckFrame from 'assets/homepage-redesign/frames/check-frame.webp'
import RocketFrame from 'assets/homepage-redesign/frames/rocket-frame.webp'
import BookFrame from 'assets/homepage-redesign/frames/book-frame.webp'
import NextImage from 'components/elements/NextImage'
import { parseTranslationInterpolation } from 'lib/features/translation'
import { AnalyticsEventHomepageHeaderClick } from 'lib/constants/AnalyticsEventType'
import { trackEvent } from 'lib/tracking/analytics-service'

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 30px 20px 100px;
    background: transparent;
    border-radius: 0px;
    position: relative;
    overflow: hidden;
    border-radius: ${theme.borderRadius.rounded};

    height: 90vh;

    @media (max-width: 940px) {
        background-color: #14183a;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: fit-content;
        padding: 100px 20px 80px;
    }
    @media ${deviceSize.sm} {
        padding: 100px 16px 20px;
    }
    @media (max-width: 380px) {
        padding: 70px 16px 20px;
    }
    @media (min-height: 1080px) or (min-width: 2160px) {
        height: 800px;
        background-color: #14183a;
    }
`

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    max-width: 800px;
    height: 100%;
    width: 100%;
    z-index: 0;
    gap: 25px;
    margin: auto;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    * > div {
        z-index: 2;
    }

    @media (max-width: 940px) {
        position: static;
        transform: none;
        margin: 0 auto;
        padding: 0px;
        height: auto;
        gap: 30px;
    }

    @media ${deviceSize.sm} {
        gap: 10px;
        width: 100%;
    }
`

const GradientHeadline = styled(Headline1)`
    z-index: 99;
    padding: 0 15%;
    font-size: 38px;
    font-weight: 550;
    line-height: 1.1;
    z-index: 10;
    span {
        all: inherit;
        padding: 0;
        display: inline;
        background: linear-gradient(90deg, #9899e4 0%, #9a76cf 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        text-align: center;
        font-weight: 650 !important;
    }

    @media ${deviceSize.md} {
        font-size: 36px;
        line-height: 1.2;
        padding: 0px;
        font-weight: 550;
        span {
            font-size: 36px !important;
            line-height: 1.2 !important;
            font-weight: 650 !important;
        }
    }
`

const BottomHomePageStats = styled.div`
    width: 100%;
    max-width: 1200px;
    display: grid;
    position: absolute;
    bottom: 30px;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 20px;
    z-index: 2;
    padding: 0 20px;

    @media (max-width: 940px) {
        position: static;
        width: 100vw;
        margin-top: 40px;
    }

    @media ${deviceSize.sm} {
        gap: 10px;
        padding: 0 10px;
        margin-top: 30px;
    }
    @media (max-width: 390px) {
        margin-top: 20px;
        gap: 2px;
        padding: 0 5px;
    }
`

const StatCard = styled.div`
    padding: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    h3,
    p {
        color: #b6b4eb;
        font-family: var(--font-inter);
        font-feature-settings: var(--inter-font-features);
    }
    p {
        font-size: 12px;
        line-height: 1.4;
        font-weight: 450;
        color: #b6b4eb;
    }
    h3 {
        white-space: nowrap;
        font-size: 17px;
        line-height: 1.4;
    }

    @media ${deviceSize.sm} {
        padding: 8px 6px;

        h3 {
            font-size: 16px;
            line-height: 1.2;
            margin-bottom: 4px;
            font-weight: 550 !important;
        }

        p {
            font-size: 10px;
            line-height: 1.2;
            font-weight: 400;
        }
    }
`

const float1 = keyframes`
    0% { transform: translate(0, 0) scale(0.9); }
    25% { transform: translate(15px, -15px) scale(0.9); }
    50% { transform: translate(0, -20px) scale(0.9); }
    75% { transform: translate(-15px, -15px) scale(0.9); }
    100% { transform: translate(0, 0) scale(0.9); }
`

const float2 = keyframes`
    0% { transform: translate(0, 0) scale(0.9); }
    25% { transform: translate(-15px, 15px) scale(0.9); }
    50% { transform: translate(0, 20px) scale(0.9); }
    75% { transform: translate(15px, 15px) scale(0.9); }
    100% { transform: translate(0, 0) scale(0.9); }
`

const float3 = keyframes`
    0% { transform: translate(0, 0) scale(0.9); }
    33% { transform: translate(20px, 10px) scale(0.9); }
    66% { transform: translate(-20px, 10px) scale(0.9); }
    100% { transform: translate(0, 0) scale(0.9); }
`

const float4 = keyframes`
    0% { transform: translate(0, 0) scale(0.9); }
    33% { transform: translate(-10px, -15px) scale(0.9); }
    66% { transform: translate(10px, -15px) scale(0.9); }
    100% { transform: translate(0, 0) scale(0.9); }
`

const floatAnimations = [float1, float2, float3, float4]

interface FloatingFrameProps {
    animationIndex: number
    position: string
}

interface FrameWithMouseRepelProps extends React.HTMLAttributes<HTMLDivElement> {
    animationIndex: number
    position: string
    children: React.ReactNode
}

const FrameWithMouseRepel: React.FC<FrameWithMouseRepelProps> = ({ animationIndex, position, children, ...props }) => {
    const frameRef = useRef<HTMLDivElement>(null)
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 940

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!frameRef.current || isMobile) return

        const element = frameRef.current
        const rect = element.getBoundingClientRect()

        const centerX = rect.left + rect.width / 2
        const centerY = rect.top + rect.height / 2

        const vectorX = centerX - e.clientX
        const vectorY = centerY - e.clientY

        const distance = Math.sqrt(vectorX * vectorX + vectorY * vectorY)

        const maxDistance = Math.sqrt(rect.width * rect.width + rect.height * rect.height) / 2

        const intensity = 30
        const scale = Math.max(0, (maxDistance - distance) / maxDistance)

        const moveX = (vectorX / maxDistance) * intensity * scale
        const moveY = (vectorY / maxDistance) * intensity * scale

        const isBottom = position.includes('bottom')
        const isTop = position.includes('top')

        if (isBottom) {
            element.style.marginLeft = `${moveX}px`
            element.style.marginBottom = `${moveY}px`
        } else if (isTop) {
            element.style.marginRight = `${moveX}px`
            element.style.marginTop = `${moveY}px`
        }
    }

    const handleMouseLeave = () => {
        if (!frameRef.current || isMobile) return
        const element = frameRef.current
        element.style.animationPlayState = 'running'
        element.style.marginLeft = '0'
        element.style.marginTop = '0'
    }

    return (
        <FloatingFrame
            ref={frameRef}
            animationIndex={animationIndex}
            position={position}
            onMouseMove={isMobile ? undefined : handleMouseMove}
            onMouseLeave={isMobile ? undefined : handleMouseLeave}
            {...props}
        >
            {children}
        </FloatingFrame>
    )
}

const FloatingFrame = styled.div<FloatingFrameProps>`
    position: absolute;
    opacity: 1;
    will-change: transform;
    transform: translateZ(0);
    animation: ${(props) => floatAnimations[props.animationIndex % floatAnimations.length]} 15s ease-in-out infinite;
    ${(props) => props.position};
    z-index: 1;
    transition: filter 0.3s ease;

    perspective: 1000;
    transform-style: preserve-3d;
    box-shadow: 0 0 1px rgba(0, 0, 0, 0);
    filter: brightness(1.01);

    &:hover {
        filter: brightness(1.1);
        z-index: 10;
    }

    img {
        transition: width 0.3s ease, height 0.3s ease;
        image-rendering: -webkit-optimize-contrast;
        -webkit-backface-visibility: hidden;
    }

    @media ${deviceSize.md} {
        position: relative;
        img {
            width: 125px !important;
            height: auto !important;
        }
    }

    @media (max-width: 940px) {
        position: relative;
        animation-duration: 12s;
        margin: 0 5px;
        img {
            width: 100px !important;
            height: auto !important;
        }
    }

    @media ${deviceSize.sm} {
        img {
            width: 110px !important;
            height: auto !important;
        }
        animation-duration: 12s;
        transform-origin: center center;
        transform: translateZ(0) scale(0.9);
    }
    @media (max-width: 400px) {
        img {
            width: 85px !important;
            height: auto !important;
        }
    }
`

const DesktopFramesContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 940px) {
        display: none;
    }
`

const FramesContainer = styled.div`
    display: none;

    @media (max-width: 940px) {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-wrap: wrap;
        position: relative;

        gap: 5px;
        width: 100vw;
        height: 30vh;
        overflow: visible;
        padding: 20px 10px;
        margin: 0px;
    }

    @media ${deviceSize.sm} {
        height: 35vh;
    }
    @media (min-height: 1024px) {
        height: 200px;
    }
`

const AppButton = styled(NewButton)`
    width: fit-content;

    @media ${deviceSize.sm} {
        height: 48px !important;
        margin: 10px 0;

        p {
            font-size: 18px !important;
        }
    }
`
const StyledSpan = styled.span``

const frameData = [
    {
        width: 192,
        height: 140,
        src: FlameFrame.src,
        alt: 'Frame 42',
        position: 'top: 15%; left: 15%;',
        delay: '0s',
    },
    {
        width: 192,
        height: 140,
        src: HeartFrame.src,
        alt: 'Frame 43',
        position: 'top: 12%; right: 10%;',
        delay: '-2s',
    },
    {
        width: 192,
        height: 140,
        src: GraduationFrame.src,
        alt: 'Frame 44',
        position: 'bottom: 25%; right: 15%;',
        delay: '-4s',
    },
    {
        width: 192,
        height: 140,
        src: CheckFrame.src,
        alt: 'Frame 45',
        position: 'bottom: 15%; left: 25%;',
        delay: '-6s',
    },
    {
        width: 192,
        height: 140,
        src: RocketFrame.src,
        alt: 'Education Hat Frame',
        position: 'top: 42.5%; left: 3%;',
        delay: '-3s',
    },
    {
        width: 192,
        height: 140,
        src: BookFrame.src,
        alt: 'Book Frame',
        position: 'top: 10%; left: 45%;',
        delay: '-1s',
    },
]

interface HomepageHeaderProps {
    onButtonClick: () => void
}

const HomepageHeader = ({ onButtonClick }: HomepageHeaderProps) => {
    const { t } = _useTranslation('homepage')
    const { locale } = useRouter()
    const parsedLocale = parseLocale(locale)

    return (
        <Container>
            <DesktopFramesContainer>
                {frameData.map((frame, index) => (
                    <FrameWithMouseRepel
                        key={index}
                        animationIndex={index}
                        position={frame.position}
                        style={{ animationDelay: frame.delay }}
                        className="desktop-frame"
                    >
                        <img src={frame.src} alt={frame.alt} width={frame.width} height={frame.height} loading="eager" />
                    </FrameWithMouseRepel>
                ))}
            </DesktopFramesContainer>

            <ContentWrapper>
                <GradientHeadline textAlign="center" color={theme.colors.white} fontFamily="var(--font-inter)">
                    {parseTranslationInterpolation(t('homepage/headerTitle'), [
                        {
                            index: 1,
                            component: StyledSpan,
                            props: {},
                        },
                    ])}
                </GradientHeadline>

                <FramesContainer>
                    {frameData.slice(0, 6).map((frame, index) => (
                        <FrameWithMouseRepel
                            key={`mobile-${index}`}
                            animationIndex={index}
                            position=""
                            style={{ animationDelay: frame.delay }}
                            className="mobile-frame"
                        >
                            <NextImage src={frame.src} alt={frame.alt} width={120} height={100} loading="eager" />
                        </FrameWithMouseRepel>
                    ))}
                </FramesContainer>

                <AppButton
                    onClick={() => {
                        trackEvent(AnalyticsEventHomepageHeaderClick, { value: 'get_app_button' })
                        onButtonClick()
                    }}
                    color={theme.colors.white}
                    borderRadius={theme.borderRadius.webappButton}
                    height={50}
                    width="fit-content"
                >
                    <Body2 color={theme.colors.black} fontWeight={600} fontSize={20} fontFamily="var(--font-inter)">
                        {t('homepage/getAppButton')}
                    </Body2>
                </AppButton>
                <BottomHomePageStats>
                    <StatCard>
                        <Headline3 textAlign="center" color={theme.colors.black} fontWeight={700}>
                            {t('homepage/statisticsStudentCount', {
                                ammount: localizeNumber(
                                    KnowunityNumbers.downloadMillionCount,
                                    parsedLocale.interfaceLanguageCode
                                ),
                            })}
                        </Headline3>
                        <Body2 textAlign="center" color={theme.colors.black}>
                            {t('homepage/statisticsStudentText')}
                        </Body2>
                    </StatCard>

                    <StatCard>
                        <Headline3 textAlign="center" color={theme.colors.black} fontWeight={700}>
                            {t('homepage/statisticsStudentCount', {
                                ammount: localizeNumber(
                                    KnowunityNumbers.knowMillionCount,
                                    parsedLocale.interfaceLanguageCode
                                ),
                            })}
                        </Headline3>
                        <Body2 textAlign="center" color={theme.colors.black}>
                            {t('homepage/statisticsLearningMaterialsText')}
                        </Body2>
                    </StatCard>

                    <StatCard>
                        <Headline3 textAlign="center" color={theme.colors.black} fontWeight={700}>
                            {KnowunityNumbers.activeCountryCount}
                        </Headline3>
                        <Body2 textAlign="center" color={theme.colors.black}>
                            {t('homepage/statisticsCountriesOnlineText')}
                        </Body2>
                    </StatCard>
                    <StatCard>
                        <Headline3 textAlign="center" color={theme.colors.black} fontWeight={700}>
                            {t('homepage/statisticsNewUsersCount', {
                                ammount: localizeNumber(KnowunityNumbers.newUsersWeekly, parsedLocale.interfaceLanguageCode),
                            })}
                        </Headline3>
                        <Body2 textAlign="center" color={theme.colors.black}>
                            {t('homepage/statisticsNewUsersText')}
                        </Body2>
                    </StatCard>
                </BottomHomePageStats>
            </ContentWrapper>
        </Container>
    )
}

export default HomepageHeader
